import * as React from 'react';

import { useQuery } from '@apollo/client';
import ProductDetailsQuery, {
  ProductDetails,
  ProductDetailsResponse,
} from '@graphql/queries/ProductDetailsQuery';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { handleAzureMigratedImages } from 'containers/Product/helper';

import styles from './ProductInfoDialog.module.css';

export interface ProductInfoRow {
  id: number;
  workspace: string;
  category: string | null;
  reviews: number;
}

export interface ProductInfoDialogProps {
  /** The product name and its workspace rows; `null` closes the dialog. */
  product: { name: string; rows: ProductInfoRow[] } | null;
  allowedWorkspaces?: string[];
  onClose: () => void;
}

const isBlank = (v: unknown) => v === null || v === undefined || v === '' || v === 'None';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : null);

/** `{"Calories": "95 kcal", "additional": [{"key": "ABV", "value": "4%"}]}` → label/value pairs. */
export const nutritionRows = (info: Record<string, unknown> | null): [string, string][] => {
  if (!info || typeof info !== 'object') return [];
  const rows: [string, string][] = [];
  Object.entries(info).forEach(([key, value]) => {
    if (key === 'additional' && Array.isArray(value)) {
      value.forEach((item) => {
        if (item && typeof item === 'object' && 'key' in item && !isBlank((item as { value?: unknown }).value)) {
          rows.push([String((item as { key: unknown }).key), String((item as { value: unknown }).value)]);
        }
      });
    } else if (!isBlank(value) && typeof value !== 'object') {
      rows.push([key, String(value)]);
    }
  });
  return rows;
};

const Facts: React.FC<{ items: [string, React.ReactNode][] }> = ({ items }) => {
  const shown = items.filter(([, v]) => !isBlank(v));
  if (shown.length === 0) return null;
  return (
    <dl className={styles.facts}>
      {shown.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const Photos: React.FC<{ urls: string[]; name: string }> = ({ urls, name }) => {
  const [active, setActive] = React.useState(0);
  if (urls.length === 0) {
    return (
      <Box className={`${styles.photo} ${styles.noPhoto}`} aria-label="No photo">
        <ImageNotSupportedOutlinedIcon fontSize="large" color="disabled" />
        <Typography variant="caption" color="text.secondary">
          No photo
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <img className={styles.photo} src={urls[Math.min(active, urls.length - 1)]} alt={name} />
      {urls.length > 1 && (
        <Box className={styles.thumbs}>
          {urls.map((url, i) => (
            <button
              key={url}
              type="button"
              className={i === active ? `${styles.thumb} ${styles.thumbActive}` : styles.thumb}
              onClick={() => setActive(i)}
              aria-label={`Photo ${i + 1}`}
              aria-pressed={i === active}
            >
              <img src={url} alt="" />
            </button>
          ))}
        </Box>
      )}
    </Box>
  );
};

const Details: React.FC<{ product: ProductDetails }> = ({ product }) => {
  const nutrition = nutritionRows(product.nutritionalInformation);
  const flags = [product.prototype && 'Prototype', product.public && 'Public', product.aroma && 'Aroma'].filter(
    Boolean,
  ) as string[];
  return (
    <Box className={styles.details}>
      {flags.length > 0 && (
        <Box className={styles.flags}>
          {flags.map((f) => (
            <Chip key={f} size="small" label={f} />
          ))}
        </Box>
      )}
      <Facts
        items={[
          ['Brand', product.brand],
          ['Local name', product.localName],
          ['Category', product.category?.name],
          ['Country', product.country],
          ['Bought in', product.countryOfPurchase !== product.country ? product.countryOfPurchase : null],
          ['Physical state', product.physicalState],
          ['Serving vessel', product.servingVessel],
          ['Dietary', product.dietaryRestrictions],
          ['Restrictions', product.restrictions],
          ['Reviews', product.productReviews.totalCount.toLocaleString()],
          ['Created', formatDate(product.createdAt)],
          ['Product ID', String(product.id)],
        ]}
      />
      {!isBlank(product.ingredients) && (
        <Box>
          <Typography variant="subtitle2">Ingredients</Typography>
          <Typography variant="body2" color="text.secondary" className={styles.prose}>
            {product.ingredients}
          </Typography>
        </Box>
      )}
      {nutrition.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Nutrition</Typography>
          <Facts items={nutrition} />
        </Box>
      )}
    </Box>
  );
};

/**
 * Everything known about a selected product: photos, catalog facts, ingredients
 * and nutrition, per workspace that has it.
 */
const ProductInfoDialog: React.FC<ProductInfoDialogProps> = ({ product, allowedWorkspaces = [], onClose }) => {
  const [tab, setTab] = React.useState(0);
  React.useEffect(() => setTab(0), [product?.name]);
  const rows = product?.rows ?? [];
  const row = rows[Math.min(tab, rows.length - 1)];
  const { data, loading, error } = useQuery<ProductDetailsResponse>(ProductDetailsQuery, {
    variables: { id: row?.id },
    skip: !row,
  });
  const details = data?.product?.id === row?.id ? data?.product : undefined;
  const photos = (details?.productImages.nodes ?? [])
    .map((n) => handleAzureMigratedImages(n.url))
    .filter((u): u is string => !!u);

  return (
    <Dialog open={!!product} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="product-info-title">
      <DialogTitle id="product-info-title" className={styles.title}>
        <span>{product?.name}</span>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      {rows.length > 1 && (
        <Tabs
          value={Math.min(tab, rows.length - 1)}
          onChange={(_, v: number) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Workspaces with this product"
          className={styles.tabs}
        >
          {rows.map((r) => (
            <Tab
              key={r.id}
              label={allowedWorkspaces.includes(r.workspace) ? r.workspace : `${r.workspace} (not allowed)`}
            />
          ))}
        </Tabs>
      )}
      <DialogContent>
        {error && <Alert severity="error">Product details failed to load: {error.message}</Alert>}
        <Box className={styles.layout}>
          {loading || !details ? (
            <Skeleton variant="rounded" className={styles.photo} />
          ) : (
            <Photos key={details.id} urls={photos} name={details.name} />
          )}
          {loading || !details ? (
            <Box aria-label="Loading product details">
              <Skeleton width="60%" />
              <Skeleton width="80%" />
              <Skeleton width="40%" />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {details.producer?.name ?? row?.workspace}
              </Typography>
              <Details product={details} />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProductInfoDialog;
